import { ObjectId } from 'bson';
import { toJS } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useHistory, useParams } from 'react-router';

import type { ITTDSubAdvertiser } from '@feathr/blackbox';
import { Button, toast, Tooltip } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import EditAdvertiser, {
  validateAdvertiser,
} from '@feathr/extender/App/Settings/Account/AdvertisersPage/EditAdvertiser';
import { useAccount, useLocalUrl } from '@feathr/extender/state';

function createAdvertiser(advId?: string): ITTDSubAdvertiser {
  return {
    subadv_id: advId || new ObjectId().toHexString(),
    logo: '',
    url: '',
    name: '',
    id: '',
  };
}

function AdvertiserPage(): JSX.Element {
  const { advertiserId } = useParams<{ advertiserId: string }>();
  const account = useAccount();
  const history = useHistory();
  const localUrl = useLocalUrl();

  const store = useLocalObservable(() => ({
    advertiser:
      advertiserId === 'add'
        ? createAdvertiser()
        : account.getAdvertiser(advertiserId) || createAdvertiser(advertiserId),
  }));

  const validation = validateAdvertiser(store.advertiser);
  const hasErrors = validation && validation.errors && Object.keys(validation.errors).length > 0;
  const errors = hasErrors
    ? Object.entries(validation.errors)
        .filter(([, value]) => value)
        .map(([key, value]) => `${key}: ${value}`)
    : [];

  async function handleSave(): Promise<void> {
    if (!account) {
      return;
    }
    if (advertiserId === 'add') {
      const ttd = account.get('ttd');
      if (!ttd) {
        account.set({ ttd: { subadvertisers: [] } });
      }
      (account.get('ttd')?.subadvertisers || []).push(toJS(store.advertiser));
    }
    account.setAttributeDirty('ttd');
    await account.patch({ ttd: account.get('ttd') });
    if (account.isErrored) {
      toast("Couldn't save advertiser. Try again.", { type: 'error' });
    } else {
      toast('Saved advertiser.', { type: 'success' });
    }
    if (advertiserId === 'add') {
      history.push(localUrl('settings/account/advertisers'));
    }
  }

  return (
    <Page
      actions={
        !hasErrors ? (
          <Button onClick={handleSave} type={'primary'}>
            Save changes
          </Button>
        ) : (
          <Tooltip
            title={errors.map((e) => (
              <div key={e}>{e as string}</div>
            ))}
          >
            <Button disabled={true} type={'primary'}>
              Save changes
            </Button>
          </Tooltip>
        )
      }
      title={store.advertiser.name || 'Unnamed Advertiser'}
    >
      <EditAdvertiser advertiser={store.advertiser} />
    </Page>
  );
}

export default observer(AdvertiserPage);
